import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Users, MapPin, Sparkles, Search } from "lucide-react";
import TopNav from "../components/TopNav";
import Orbs from "../components/Orbs";
import { Section, Overline } from "../components/Layout";
import { api } from "../lib/api";
import { fmtTimePT, fmtDatePT, fmtWeekdayPT, fmtFullDatePT, yearPT } from "../lib/time";
import { resolveImageUrl } from "../lib/media";
import { captureUtmFromLocation } from "../lib/utm";
import { myRegistrations } from "../lib/register";

const AUD_ALL = "all";
const AUD_PUBLIC = "public";
const AUD_MEMBERS = "members_only";

// Maps a registration status to its card badge. Anything not listed (e.g.
// cancelled, which the API already filters out) renders no badge.
const MY_STATUS_BADGE = {
  going:      { label: "Registered",   bg: "rgba(61,115,106,0.92)",  fg: "#fff" },
  checked_in: { label: "Attended",     bg: "rgba(61,115,106,0.92)",  fg: "#fff" },
  pending:    { label: "Under review", bg: "rgba(255,244,231,0.95)", fg: "hsl(var(--vd-peach-ink))" },
  waitlist:   { label: "Waitlisted",   bg: "rgba(175,138,216,0.92)", fg: "#fff" },
  invited:    { label: "Invited",      bg: "rgba(255,244,231,0.95)", fg: "hsl(var(--vd-peach-ink))" },
  booked:     { label: "Booked",       bg: "rgba(61,115,106,0.92)",  fg: "#fff" },
};

function MyBadge({ status }) {
  const b = MY_STATUS_BADGE[status];
  if (!b) return null;
  return (
    <div className="absolute top-3 right-3 z-10">
      <span className="chip border-white/30 font-medium"
            style={{ background: b.bg, color: b.fg, backdropFilter: "blur(8px)" }}
            data-testid={`my-status-${status}`}>
        {b.label}
      </span>
    </div>
  );
}

const stripHtml = (v) => {
  if (!v || typeof v !== "string") return "";
  return v.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
};

export default function Events() {
  const [events, setEvents] = useState(null);
  // program_id -> badge_label for 1:1 session cards (Founder Labs, …)
  const [programBadges, setProgramBadges] = useState({});
  const [audience, setAudience] = useState(AUD_ALL);
  // Time window is a SEPARATE dimension from the audience chips (public vs
  // members-only), so it gets its own control rather than a fourth chip.
  // Server-side: ended events are filtered out of the default response, so this
  // cannot be done by filtering the existing list on the client.
  const [when, setWhen] = useState("upcoming");
  const [q, setQ] = useState("");
  // { events: {id: status}, series: {id: status} } for the verified visitor, or
  // null when signed out / session expired — in which case no badges render.
  const [mine, setMine] = useState(null);

  useEffect(() => {
    captureUtmFromLocation();
    api.get("/programs").then((r) => setProgramBadges(Object.fromEntries((r.data || []).map((p) => [p.id, p.badge_label || p.name])))).catch(() => {});
    myRegistrations().then(setMine).catch(() => {});
  }, []);

  // Refetch when the window changes — `past` is a different server response,
  // not a client-side slice of the same one.
  useEffect(() => {
    setEvents(null);
    api.get("/events", { params: { when } }).then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, [when]);

  // Phase 4e: 1:1 sessions are ordinary events on the grid now (format='slots');
  // the separate series cards are gone.
  const items = useMemo(() => {
    let list = (events || []).map((e) => ({ kind: "event", id: e.id, sort_at: e.start_at || "2100-01-01", data: e }));
    if (audience !== AUD_ALL) list = list.filter((it) => it.data.visibility === audience);
    if (q) list = list.filter((it) => it.data.title?.toLowerCase().includes(q.toLowerCase()));
    return list.sort((a, b) => (when === "past"
      ? b.sort_at.localeCompare(a.sort_at)   // archive: most recent first
      : a.sort_at.localeCompare(b.sort_at)));
  }, [events, audience, q, when]);

  return (
    <div className="relative min-h-screen" data-testid="events-page">
      <Orbs />
      <div className="relative z-10">
        <TopNav variant="public" />
        <Section className="pt-14 pb-8">
          <div className="flex flex-col items-center text-center">
            <Overline>Venture Dock Events</Overline>
            <h1 className="mt-3 text-5xl sm:text-6xl font-heading font-semibold tracking-tighter max-w-3xl">Where founders show up.</h1>
            <p className="text-muted-foreground mt-4 max-w-xl">Pitch nights, workshops, and Founder Labs investor sessions — hosted at our Palo Alto and Milpitas campuses.</p>
          </div>
        </Section>

        <Section className="pb-12">
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="flex items-center gap-2" data-testid="audience-filter">
              {[{ k: AUD_ALL, label: "All" }, { k: AUD_PUBLIC, label: "Public" }, { k: AUD_MEMBERS, label: "Members-only" }].map((o) => (
                <button key={o.k} onClick={() => setAudience(o.k)} data-testid={`aud-${o.k}`}
                        className={`px-3.5 py-1.5 rounded-full text-sm border transition ${audience === o.k ? "bg-[hsl(var(--vd-green))] text-white border-[hsl(var(--vd-green))]" : "bg-white/60 border-white/60 hover:bg-white/85"}`}>
                  {o.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="segmented" data-testid="when-filter">
                {[{ k: "upcoming", label: "Upcoming" }, { k: "past", label: "Past" }].map((o) => (
                  <button key={o.k} onClick={() => setWhen(o.k)} data-testid={`when-${o.k}`}
                          className={`segmented-btn text-sm ${when === o.k ? "active" : ""}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input className="input-field pl-9 w-60" placeholder="Search events…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="events-search" />
            </div>
            </div>
          </div>

          {!events ? (
            <div className="mt-8 grid md:grid-cols-3 gap-5">{[...Array(6)].map((_, i) => <div key={i} className="skeleton h-72" />)}</div>
          ) : items.length === 0 ? (
            <div className="mt-8 card-cream p-12 text-center text-muted-foreground">
              {when === "past" ? "No past events match your filter." : "No upcoming events match your filter."}
            </div>
          ) : (
            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((it) => (
                <EventCard key={it.id} e={it.data} myStatus={mine?.events?.[it.id]}
                  programBadge={it.data.format === "slots" ? (programBadges[it.data.program_id] || "1:1 Session") : null} />
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

function EventCard({ e, myStatus, programBadge }) {
  const spotsLeft = e.capacity != null ? Math.max(0, e.capacity - (e.registration_count || 0)) : null;
  const full = spotsLeft === 0;
  // Luma-hosted events in OUTLINK mode link OUT to lu.ma — Luma is the writable
  // home for their RSVPs (L1). Native-mode Luma events (L2) and everything else
  // route to our own detail/registration flow; their RSVPs write BACK to Luma.
  const isLuma = e.source === "luma";
  const isOutlink = isLuma && e.rsvp_mode_effective !== "native";
  const Wrapper = isOutlink ? "a" : Link;
  const wrapperProps = isOutlink
    ? { href: e.luma_url, target: "_blank", rel: "noreferrer" }
    : { to: e.format === "slots" ? `/events/capital/${e.id}` : `/events/${e.short_slug || e.id}` };
  return (
    <Wrapper {...wrapperProps} className="glass-panel overflow-hidden hover-lift group relative block" data-testid={`event-card-${e.id}`}>
      {/* Luma covers are SQUARE — 1200x1200, measured on the live CDN. Forcing
          them into a 16:10 box with object-cover cropped ~40% of each image,
          which is why imported cards looked trimmed. Native covers are authored
          for the wide box, so the ratio follows the source rather than being
          one-size-fits-all. */}
      <div className={`relative overflow-hidden ${e.source === "luma" ? "aspect-square" : "aspect-[16/10]"}`}>
        <MyBadge status={myStatus} />
        {e.cover_image ? <img src={resolveImageUrl(e.cover_image)} alt={e.title} className="h-full w-full object-cover group-hover:scale-[1.04] transition-transform duration-700" /> : <div className="aurora-cover w-full h-full" />}
        <div className="absolute inset-x-0 bottom-0 h-1/2" style={{ background: "linear-gradient(180deg, transparent, rgba(0,0,0,0.55))" }} />
        <div className="absolute top-3 left-3 flex flex-col items-center rounded-xl overflow-hidden font-mono" style={{ background: "rgba(255,255,255,0.9)", backdropFilter: "blur(8px)" }}>
          <div className="px-3 pt-1.5 text-[9px] tracking-widest uppercase" style={{ color: "hsl(var(--vd-peach-ink))" }}>{fmtDatePT(e.start_at, { month: "short" })}</div>
          <div className="px-3 font-heading text-xl leading-none">{fmtDatePT(e.start_at, { day: "numeric" })}</div>
          {/* Year only when it is not the current one: essential in the Past
              archive, pure clutter on a grid of upcoming events. */}
          <div className="px-3 pb-1.5 text-[9px] tracking-widest tabular-nums" style={{ color: "hsl(var(--vd-peach-ink))" }}>
            {yearPT(e.start_at) !== String(new Date().getFullYear()) ? yearPT(e.start_at) : "\u00A0"}
          </div>
        </div>
        <div className="absolute bottom-3 left-3 flex gap-1.5 flex-wrap">
          <span className="chip text-white border-white/30" style={{ background: e.visibility === "public" ? "rgba(246,105,60,0.9)" : e.visibility === "members_only" ? "rgba(175,138,216,0.9)" : "rgba(161,50,92,0.9)" }} data-testid={`audience-badge-${e.id}`}>
            {e.visibility === "public" ? "Public" : e.visibility === "members_only" ? "Members" : "Internal"}
          </span>
          {programBadge && <span className="chip text-white border-white/30" style={{ background: "rgba(61,115,106,0.92)" }} data-testid={`program-badge-${e.id}`}>{programBadge}</span>}
          {isOutlink && <span className="chip text-white border-white/30" style={{ background: "rgba(175,138,216,0.92)" }} data-testid={`luma-badge-${e.id}`}>RSVP on Luma ↗</span>}
          {isLuma && !isOutlink && <span className="chip text-white border-white/30" style={{ background: "rgba(175,138,216,0.92)" }} data-testid={`luma-badge-${e.id}`}>Luma</span>}
          {e.require_approval && <span className="chip" style={{ background: "rgba(255,244,231,0.9)", color: "hsl(var(--vd-peach-ink))" }}>Approval</span>}
        </div>
      </div>
      <div className="p-5">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 font-mono text-xs uppercase text-muted-foreground">
          <span>{fmtWeekdayPT(e.start_at).slice(0, 3)}</span><span>·</span><span>{fmtFullDatePT(e.start_at)}</span><span>·</span><span>{fmtTimePT(e.start_at)}</span><span>·</span><span style={{ color: "hsl(var(--vd-green))" }}>PT</span>
        </div>
        <h3 className="mt-2 font-heading text-xl leading-snug">{e.title}</h3>
        <p className="text-sm text-muted-foreground mt-1.5 line-clamp-1">{stripHtml(e.description).split("\n")[0]}</p>
        <div className="mt-4 flex items-center justify-between text-xs">
          <span className="flex items-center gap-1.5 text-muted-foreground"><MapPin size={11} />{e.campus_id === "cmp_pa" ? "Palo Alto" : e.campus_id === "cmp_mp" ? "Milpitas" : e.custom_venue || (e.virtual_link ? "Virtual" : "TBD")}</span>
          <span className="flex items-center gap-1.5 font-mono" style={{ color: full ? "hsl(var(--vd-peach-ink))" : "hsl(var(--vd-green))" }}><Users size={11} />{e.format === "slots" ? `${e.slots_open ?? 0} slot${(e.slots_open ?? 0) === 1 ? "" : "s"} open` : spotsLeft == null ? `${e.registration_count || 0} going` : full && e.waitlist_enabled ? "Waitlist open" : full ? "Sold out" : `${spotsLeft} spots left`}</span>
        </div>
      </div>
    </Wrapper>
  );
}

